/* eslint-disable @typescript-eslint/no-unused-vars */
import { type TemplateCategory } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

const sessionStorageForTemplateId = createJSONStorage<string | null>(
  () => sessionStorage
);

/**
 * Stores the source template's category when creating a custom copy from a Heidi template.
 * This is needed because when editing a Heidi template, we create a new templateCreationRecord
 * without a template_id, so the original template's category would otherwise be lost.
 */
export const sourceTemplateCategoryAtom = atom<TemplateCategory | undefined>(undefined);

/**
 * Atom family that stores the currently selected version control ID for each template creation record.
 * This allows tracking which version of the template structure/example is currently being viewed or edited.
 * @param templateCreationRecordId - The ID of the template creation record
 * @returns An atom containing the selected version control ID, or undefined if none selected
 */
export const selectedVersionControlIdAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_templateCreationRecordId: string) => atom<string | undefined>(undefined)
);

/**
 * Tracks whether the application is currently navigating between routes.
 * Used to prevent duplicate navigation actions.
 * @default false
 */
export const isNavigatingAtom = atom(false);

// Reference-counted so concurrent conversion owners cannot clear each other's
// pending state. A boolean would drop to idle when the first owner finishes.
// Each owner must write true once and false once. A second true without a
// matching false latches consumers disabled; an extra false while another
// owner is still running is out of contract (the count is not owner-keyed).
// Production writers land in the conversion-runner PR of this stack; this PR
// only reads the atom to gate save and form disable.
const useSectionsConversionPendingCountAtom = atom(0);

export const isUseSectionsConversionPendingAtom = atom(
  (get) => get(useSectionsConversionPendingCountAtom) > 0,
  (get, set, pending: boolean) => {
    const count = get(useSectionsConversionPendingCountAtom);
    set(
      useSectionsConversionPendingCountAtom,
      pending ? count + 1 : Math.max(0, count - 1)
    );
  }
);

/**
 * Stores the ID of the current template creation record being edited.
 * This is the main identifier used to fetch and update template data.
 * @default undefined
 */
export const templateCreationRecordIdAtom = atom<string | undefined>(undefined);

/**
 * Tracks whether the template form has been initialized with data.
 * Used to prevent re-initialization and ensure proper form state management.
 * @default false
 */
export const hasInitializedFormAtom = atom<boolean>(false);

/**
 * Tracks whether the user has confirmed navigation away from the template editor.
 * Used with unsaved changes warning modals to allow navigation after user confirmation.
 * @default false
 */
export const isNavigationBlockDisabledAtom = atom(false);

/**
 * Stores the route the user is attempting to navigate to when there are unsaved changes.
 * Used to resume navigation after the user confirms they want to leave.
 * @default null
 */
export const pendingRouteAtom = atom<string | null>(null);

/**
 * Controls which tab is currently active in the template details view.
 * @default 'example'
 */
export const activeTabAtom = atom<'example' | 'structure'>('example');

/**
 * Controls the visibility of the syntax helper panel.
 * Hidden on non-lg screens.
 * @default false
 */
export const isHelperOpenAtom = atom(false);

/**
 * Tracks whether the user navigated to the template editor from the create document modal.
 * Used to customize the navigation flow and return behavior.
 * @default false
 */
export const isFromCreateDocumentModalAtom = atom(false);

/**
 * Controls the visibility of the regulatory guideline warning modal (v2).
 * Shown when users create or update templates that may conflict with regulatory guidelines.
 * @default false
 */
export const isRegulatoryGuidelineWarningModalV2OpenAtom = atom(false);

/** The checker finding used to target the hidden compliance repair request. */
export const regulatoryGuidelineComplianceReasonAtom = atom<string | null>(null);

/**
 * Tracks whether the user navigated from a template share link import.
 * Used to show the "Template added!" toast with "Use in a session" action.
 * @default false
 */
export const isFromTemplateShareImportAtom = atom(false);

/**
 * Signals that the user clicked "Use in a session" from the template added toast.
 */
export const triggerUseInSessionAtom = atom(false);

/**
 * Stores the template ID to apply to the next created session.
 * Used when user clicks "Use in a session" from the template added toast.
 * Persisted in sessionStorage to survive navigation.
 */
export const pendingSessionTemplateIdAtom = atomWithStorage<string | null>(
  'heidi-pending-session-template-id',
  null,
  sessionStorageForTemplateId
);

/**
 * Signals that the current save operation is from the "Use in a session" flow.
 * When true, onSubmit will set pendingSessionTemplateIdAtom before navigating.
 */
export const useInSessionFlowAtom = atom(false);

/**
 * Stores pending form changes that need to be carried over during template clone navigation.
 * When a user without permission edits a template, the system creates a clone.
 * This atom preserves the user's changes so they can be restored after navigation.
 * @default null
 */
export const pendingFormChangesAtom = atom<Record<string, unknown> | null>(null);
